import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Button, Grid,
  List, ListItem, ListItemButton, ListItemText, Divider, Collapse
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import GavelIcon from '@mui/icons-material/Gavel';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { getStandingOrderByNumber, getStandingOrdersToc } from '../services/api';

// Component to render a single standing order
function StandingOrderContent({ standingOrder, isFirst }) {
  return (
    <Box
      id={`so-${standingOrder.so_number}`}
      sx={{
        scrollMarginTop: '80px',
        mb: 3,
        pb: 3,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {isFirst && <GavelIcon sx={{ color: '#5c6bc0', fontSize: 28 }} />}
        <Typography variant={isFirst ? 'h5' : 'h6'} sx={{ fontWeight: 600, color: '#5c6bc0' }}>
          Standing Order {standingOrder.so_number}
        </Typography>
      </Box>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
        {standingOrder.title}
      </Typography>

      {!isFirst && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {standingOrder.section_title && (
            <Chip label={standingOrder.section_title} variant="outlined" size="small" />
          )}
        </Box>
      )}

      {/* Content */}
      {standingOrder.content_html ? (
        <Box
          sx={{
            '& p': { mb: 2, lineHeight: 1.8 },
            '& ol': { pl: 3, mb: 2, listStyleType: 'none' },
            '& ul': { pl: 3, mb: 2, listStyleType: 'none' },
            '& li': { mb: 1, lineHeight: 1.7 },
            // Table styling
            '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
            '& th, & td': { padding: '8px', border: '1px solid #ddd', lineHeight: 1.6 },
            '& th': { backgroundColor: '#f5f5f5', fontWeight: 600 },
            '& thead tr': { backgroundColor: '#f5f5f5' },
            // Definition list styling
            '& dl': { mb: 2 },
            '& dt': { fontWeight: 600, mt: 1.5, color: '#333' },
            '& dd': { ml: 2, mb: 1, color: '#555', lineHeight: 1.7 }
          }}
          dangerouslySetInnerHTML={{ __html: standingOrder.content_html }}
        />
      ) : (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
          {standingOrder.content}
        </Typography>
      )}

      {/* Clauses if present and not in HTML */}
      {!standingOrder.content_html && standingOrder.clauses?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box component="ol" sx={{ pl: 3 }}>
            {standingOrder.clauses.map((clause, idx) => (
              <Box component="li" key={idx} sx={{ mb: 1 }}>
                {clause.identifier && (
                  <Typography component="span" sx={{ fontWeight: 500 }}>
                    ({clause.identifier}){' '}
                  </Typography>
                )}
                <Typography component="span">{clause.text}</Typography>
                {clause.sub_items?.length > 0 && (
                  <Box component="ol" type="i" sx={{ pl: 3, mt: 1 }}>
                    {clause.sub_items.map((sub, subIdx) => (
                      <Box component="li" key={subIdx} sx={{ mb: 0.5 }}>
                        {sub.identifier && (
                          <Typography component="span" sx={{ fontWeight: 500 }}>
                            ({sub.identifier}){' '}
                          </Typography>
                        )}
                        <Typography component="span">{sub.text}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Cross-references */}
      {standingOrder.cross_references?.length > 0 && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Cross-references
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {standingOrder.cross_references.map((ref, idx) => (
              <Chip
                key={idx}
                label={ref}
                size="small"
                variant="outlined"
                component={RouterLink}
                to={`/standingorders/${ref.replace('SO ', '').replace('s.o. ', '')}`}
                clickable
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function StandingOrdersDetail() {
  const { soNumber } = useParams();
  const navigate = useNavigate();
  const [standingOrders, setStandingOrders] = useState([]); // Array of loaded standing orders
  const [primarySo, setPrimarySo] = useState(null); // The SO user navigated to
  const [currentSoNumber, setCurrentSoNumber] = useState(soNumber); // Track current SO for TOC highlighting
  const [currentChapter, setCurrentChapter] = useState(null); // Track current chapter info (for outline)
  const [toc, setToc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tocLoading, setTocLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [expandedChapters, setExpandedChapters] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const loadedSoRef = useRef(new Set());
  const contentRef = useRef(null);

  // Load a single standing order by number
  const loadStandingOrder = useCallback(async (soNum) => {
    if (!soNum || loadedSoRef.current.has(soNum)) return null;
    try {
      const data = await getStandingOrderByNumber(soNum);
      loadedSoRef.current.add(soNum);
      return data;
    } catch (err) {
      console.error('Failed to load standing order:', soNum, err);
      return null;
    }
  }, []);

  // Load multiple standing orders and replace current view
  const loadStandingOrderGroup = useCallback(async (soNumbers, primaryNumber) => {
    if (soNumbers.length === 0) return;

    setLoadingMore(true);
    try {
      const newOrders = [];
      for (const soNum of soNumbers) {
        if (!loadedSoRef.current.has(soNum)) {
          const so = await loadStandingOrder(soNum);
          if (so) {
            newOrders.push(so);
          }
        } else {
          // Find already loaded standing order
          const existing = standingOrders.find(s => s.so_number === soNum);
          if (existing) {
            newOrders.push(existing);
          }
        }
      }

      if (newOrders.length > 0) {
        // Sort by sequence number to maintain order
        newOrders.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        setStandingOrders(newOrders);
        setCurrentSoNumber(primaryNumber);
        // Update primarySo to the first item in the new group for header display
        setPrimarySo(newOrders[0]);

        // Scroll to top of content
        if (contentRef.current) {
          contentRef.current.scrollTop = 0;
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Error loading standing order group:', err);
    }
    setLoadingMore(false);
  }, [standingOrders, loadStandingOrder]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      loadedSoRef.current = new Set();
      setStandingOrders([]);

      try {
        const data = await getStandingOrderByNumber(soNumber);
        loadedSoRef.current.add(soNumber);
        setPrimarySo(data);
        setStandingOrders([data]);
        setCurrentSoNumber(soNumber);

        // Auto-expand the current chapter in ToC
        if (data.chapter_number) {
          setExpandedChapters({ [data.chapter_number]: true });
        }
      } catch (err) {
        console.error('Error fetching standing order:', err);
        setError('Standing order not found');
      }
      setLoading(false);
    };

    const fetchToc = async () => {
      setTocLoading(true);
      try {
        const data = await getStandingOrdersToc();
        setToc(data);
      } catch (err) {
        console.error('Failed to load ToC:', err);
      }
      setTocLoading(false);
    };

    fetchData();
    fetchToc();
  }, [soNumber]);

  const handlePrev = () => {
    if (primarySo?.prev_so_id) {
      const prevNum = primarySo.prev_so_id.replace('so_', '');
      navigate(`/standingorders/${prevNum}`);
    }
  };

  const handleNext = () => {
    if (primarySo?.next_so_id) {
      const nextNum = primarySo.next_so_id.replace('so_', '');
      navigate(`/standingorders/${nextNum}`);
    }
  };

  const toggleChapter = (chapterNum) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterNum]: !prev[chapterNum]
    }));
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Collect all SO numbers from a chapter
  const collectChapterSoNumbers = (chapter) => {
    const numbers = [];
    chapter.sections?.forEach(section => {
      section.standing_orders?.forEach(so => {
        numbers.push(so.so_number);
      });
    });
    return numbers;
  };

  // Collect all SO numbers from a section
  const collectSectionSoNumbers = (section) => {
    return section.standing_orders?.map(so => so.so_number) || [];
  };

  // Handle chapter click - load all SOs in chapter
  const handleChapterClick = (chapter) => {
    const soNumbers = collectChapterSoNumbers(chapter);
    if (soNumbers.length > 0) {
      loadStandingOrderGroup(soNumbers, soNumbers[0]);
      // Store chapter info for outline display
      setCurrentChapter(chapter);
      // Expand the chapter in TOC
      setExpandedChapters(prev => ({ ...prev, [chapter.chapter_number]: true }));
    }
  };

  // Handle section click - load all SOs in section
  const handleSectionClick = (section, chapterNum) => {
    const soNumbers = collectSectionSoNumbers(section);
    if (soNumbers.length > 0) {
      loadStandingOrderGroup(soNumbers, soNumbers[0]);
      // Clear chapter info when viewing just a section
      setCurrentChapter(null);
    }
  };

  // Handle single SO click - clear chapter view
  const handleSoClick = (soNumber) => {
    loadStandingOrderGroup([soNumber], soNumber);
    setCurrentChapter(null);
  };

  // Get count of loaded SOs
  const loadedSoNumbers = standingOrders.map(so => so.so_number);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !primarySo) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error || 'Standing order not found'}</Typography>
        <Button component={RouterLink} to="/standingorders" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Standing Orders
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Navigation bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          component={RouterLink}
          to="/standingorders"
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to Standing Orders
        </Button>

        {/* Prev/Next navigation */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIosNewIcon />}
            onClick={handlePrev}
            disabled={!primarySo?.prev_so_id}
            sx={{ borderRadius: 2 }}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            size="small"
            endIcon={<ArrowForwardIosIcon />}
            onClick={handleNext}
            disabled={!primarySo?.next_so_id}
            sx={{ borderRadius: 2 }}
          >
            Next
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Header */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <GavelIcon sx={{ color: '#5c6bc0', fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#5c6bc0' }}>
                {standingOrders.length > 1
                  ? `Chapter ${primarySo.chapter_number}: ${primarySo.chapter_title}`
                  : `Standing Order ${primarySo.so_number}`}
              </Typography>
            </Box>
            {standingOrders.length === 1 && (
              <Typography variant="h6" sx={{ mb: 2 }}>
                {primarySo.title}
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip
                label={`Chapter ${primarySo.chapter_number}`}
                color="primary"
                size="small"
              />
              {primarySo.chapter_title && (
                <Chip label={primarySo.chapter_title} variant="outlined" size="small" />
              )}
              {standingOrders.length === 1 && primarySo.section_title && (
                <Chip label={primarySo.section_title} variant="outlined" size="small" />
              )}
              {standingOrders.length > 1 && (
                <Chip
                  label={`${standingOrders.length} standing orders`}
                  variant="outlined"
                  size="small"
                  color="secondary"
                />
              )}
            </Box>

            {/* Chapter outline - shown when viewing a full chapter */}
            {currentChapter?.outline && currentChapter.outline.length > 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                  This chapter covers:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {currentChapter.outline.map((item, idx) => (
                    <Typography component="li" key={idx} variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Paper>

          {/* Content */}
          <Paper ref={contentRef} elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            {/* Loading overlay */}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}

            {/* Render all loaded standing orders */}
            {standingOrders.map((so, idx) => (
              <StandingOrderContent
                key={so.so_id || so.so_number}
                standingOrder={so}
                isFirst={idx === 0}
              />
            ))}

            {/* End of content indicator when showing multiple */}
            {standingOrders.length > 1 && (
              <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                <Typography variant="caption">
                  Showing {standingOrders.length} standing orders from Chapter {primarySo.chapter_number}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Table of Contents Sidebar */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              position: 'sticky',
              top: 16,
              maxHeight: 'calc(100vh - 100px)',
              overflow: 'auto'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Table of Contents
            </Typography>

            {tocLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : toc ? (
              <List dense disablePadding>
                {toc.chapters?.map((chapter) => {
                  const chapterSoNumbers = collectChapterSoNumbers(chapter);
                  const isChapterLoaded = chapterSoNumbers.length > 0 &&
                    chapterSoNumbers.every(num => loadedSoNumbers.includes(num));
                  const hasLoadedSos = chapterSoNumbers.some(num => loadedSoNumbers.includes(num));

                  return (
                    <React.Fragment key={chapter.chapter_number}>
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => handleChapterClick(chapter)}
                          sx={{
                            py: 0.5,
                            borderRadius: 1,
                            borderLeft: isChapterLoaded ? '3px solid #5c6bc0' : hasLoadedSos ? '3px solid #e0e0e0' : '3px solid transparent',
                            bgcolor: isChapterLoaded ? 'rgba(92, 107, 192, 0.08)' : 'transparent',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.85rem',
                                  color: isChapterLoaded ? '#5c6bc0' : 'text.primary'
                                }}
                              >
                                Ch. {chapter.chapter_number}: {chapter.chapter_title}
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  ({chapterSoNumbers.length})
                                </Typography>
                              </Typography>
                            }
                          />
                          <Box
                            onClick={(e) => { e.stopPropagation(); toggleChapter(chapter.chapter_number); }}
                            sx={{ ml: 1, display: 'flex', alignItems: 'center' }}
                          >
                            {expandedChapters[chapter.chapter_number] ? (
                              <ExpandLess fontSize="small" />
                            ) : (
                              <ExpandMore fontSize="small" />
                            )}
                          </Box>
                        </ListItemButton>
                      </ListItem>
                      <Collapse in={expandedChapters[chapter.chapter_number]}>
                        <List dense disablePadding sx={{ pl: 1 }}>
                          {chapter.sections?.map((section, sIdx) => {
                            const sectionSoNumbers = collectSectionSoNumbers(section);
                            const sectionKey = `${chapter.chapter_number}-${sIdx}`;
                            const isSectionLoaded = sectionSoNumbers.length > 0 &&
                              sectionSoNumbers.every(num => loadedSoNumbers.includes(num));
                            const hasSectionLoadedSos = sectionSoNumbers.some(num => loadedSoNumbers.includes(num));

                            return (
                              <React.Fragment key={sIdx}>
                                {/* Section header - clickable to load all SOs in section */}
                                {section.section_title && sectionSoNumbers.length > 1 && (
                                  <ListItem disablePadding>
                                    <ListItemButton
                                      onClick={() => handleSectionClick(section, chapter.chapter_number)}
                                      sx={{
                                        py: 0.25,
                                        pl: 1,
                                        borderLeft: isSectionLoaded ? '3px solid #5c6bc0' : hasSectionLoadedSos ? '3px solid #e0e0e0' : '3px solid transparent',
                                        '&:hover': { bgcolor: 'action.hover' }
                                      }}
                                    >
                                      <ListItemText
                                        primary={
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 500,
                                              color: isSectionLoaded ? '#5c6bc0' : 'text.secondary'
                                            }}
                                          >
                                            {section.section_title}
                                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                              ({sectionSoNumbers.length})
                                            </Typography>
                                          </Typography>
                                        }
                                      />
                                      <Box
                                        onClick={(e) => { e.stopPropagation(); toggleSection(sectionKey); }}
                                        sx={{ ml: 1, display: 'flex', alignItems: 'center' }}
                                      >
                                        {expandedSections[sectionKey] === false ? (
                                          <ExpandMore fontSize="small" sx={{ fontSize: '1rem' }} />
                                        ) : (
                                          <ExpandLess fontSize="small" sx={{ fontSize: '1rem' }} />
                                        )}
                                      </Box>
                                    </ListItemButton>
                                  </ListItem>
                                )}
                                {/* Individual SOs */}
                                <Collapse in={expandedSections[sectionKey] !== false}>
                                  {section.standing_orders?.map((so) => {
                                    const isCurrentSo = so.so_number === currentSoNumber;
                                    const isLoaded = loadedSoNumbers.includes(so.so_number);
                                    return (
                                      <ListItem key={so.so_id} disablePadding>
                                        <ListItemButton
                                          onClick={() => handleSoClick(so.so_number)}
                                          selected={isCurrentSo}
                                          sx={{
                                            py: 0.25,
                                            pl: section.section_title && sectionSoNumbers.length > 1 ? 3 : 2,
                                            borderLeft: isCurrentSo ? '3px solid #5c6bc0' : isLoaded ? '3px solid #e0e0e0' : '3px solid transparent',
                                            '&.Mui-selected': {
                                              bgcolor: 'rgba(92, 107, 192, 0.08)'
                                            },
                                            '&:hover': { bgcolor: 'action.hover' }
                                          }}
                                        >
                                          <ListItemText
                                            primary={
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontSize: '0.8rem',
                                                  fontWeight: isCurrentSo ? 600 : 400,
                                                  color: isCurrentSo ? '#5c6bc0' : isLoaded ? '#666' : 'text.secondary'
                                                }}
                                              >
                                                SO {so.so_number}: {so.title?.substring(0, 30)}{so.title?.length > 30 ? '...' : ''}
                                              </Typography>
                                            }
                                          />
                                        </ListItemButton>
                                      </ListItem>
                                    );
                                  })}
                                </Collapse>
                              </React.Fragment>
                            );
                          })}
                        </List>
                      </Collapse>
                      <Divider sx={{ my: 0.5 }} />
                    </React.Fragment>
                  );
                })}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Failed to load table of contents
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
